import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setApiToken, setTokenRefreshCallback } from './api/authApiClient';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { beginRequesterIdentity, fetchRequesterIdentity } from './app/userInfoSlice';

const TokenContext = createContext(false);
export const useTokenReady = () => useContext(TokenContext);

const isSessionError = (error) => {
  const code = String(error?.error || '').toLowerCase();
  const message = String(error?.message || error || '').toLowerCase();
  return ['login_required', 'consent_required', 'missing_refresh_token', 'invalid_grant'].includes(code)
    || /missing refresh token|invalid state|login required|session.*expired/.test(message);
};

const currentReturnTo = () => `${window.location.pathname}${window.location.hash || ''}`;

export const TokenReadyProvider = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    user,
    loginWithRedirect,
    error,
  } = useAuth0();
  const dispatch = useDispatch();
  const redirectStarted = useRef(false);
  const subject = isAuthenticated && !isLoading && typeof user?.sub === 'string' ? user.sub : null;
  const scopeRef = useRef({ subject, generation: 0 });
  // Invalidate during render, before a newly authenticated subject can see the
  // old dashboard or an old async response can publish a token or identity.
  if (scopeRef.current.subject !== subject) {
    scopeRef.current = { subject, generation: scopeRef.current.generation + 1 };
  }
  const generation = scopeRef.current.generation;
  const [readyScope, setReadyScope] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const ready = Boolean(subject && readyScope?.subject === subject && readyScope?.generation === generation);

  const startCleanLogin = useCallback(async () => {
    if (redirectStarted.current) return;
    redirectStarted.current = true;
    setReadyScope(null);
    setApiToken(null);
    dispatch(beginRequesterIdentity(null));
    setFatalError(null);
    const loginScope = scopeRef.current;

    // Auth0Provider owns callback processing. Never process the callback twice.
    if (window.location.search.includes('code=') || window.location.search.includes('state=')) {
      window.history.replaceState({}, document.title, currentReturnTo());
    }

    try {
      await loginWithRedirect({
        appState: { returnTo: currentReturnTo() },
        authorizationParams: { prompt: 'login' },
      });
    } catch (loginError) {
      if (scopeRef.current !== loginScope) return;
      redirectStarted.current = false;
      setFatalError({ ...loginScope, message: 'Your session expired and sign-in could not be started. Reload this page to try again.' });
      console.error('Failed to restart authentication:', loginError);
    }
  }, [dispatch, loginWithRedirect]);

  useEffect(() => {
    const scope = scopeRef.current;
    let cancelled = false;
    const isCurrent = () => !cancelled && scopeRef.current === scope;
    dispatch(beginRequesterIdentity(subject));
    setReadyScope(null);
    setFatalError(null);
    setApiToken(null);
    if (isLoading || redirectStarted.current) return undefined;
    if (!isAuthenticated) {
      void startCleanLogin();
      return undefined;
    }
    if (!subject) return undefined;

    const bootstrap = async () => {
      try {
        // Auth0's redirect has already cached a valid token. Do not force an
        // immediate refresh, which can fail in privacy/incognito contexts.
        const token = await getAccessTokenSilently();
        if (!isCurrent()) return;
        setApiToken(token);
        localStorage.setItem('subId', subject);
        await dispatch(fetchRequesterIdentity(subject)).unwrap();
        if (isCurrent()) setReadyScope({ subject, generation: scope.generation });
      } catch (bootstrapError) {
        if (!isCurrent()) return;
        setApiToken(null);
        console.error('Failed to initialize authenticated dashboard:', bootstrapError);
        if (isSessionError(bootstrapError) || isSessionError(error)) {
          void startCleanLogin();
        } else {
          setFatalError({ ...scope, message: 'The dashboard could not finish loading. Reload the page to try again.' });
        }
      }
    };
    void bootstrap();
    return () => { cancelled = true; };
  }, [dispatch, error, generation, getAccessTokenSilently, isAuthenticated, isLoading, startCleanLogin, subject]);

  useEffect(() => {
    const scope = scopeRef.current;
    let cancelled = false;
    const refreshToken = async () => {
      if (cancelled || scopeRef.current !== scope || !subject) throw new Error('Authenticated identity is not ready');
      try {
        const token = await getAccessTokenSilently({ cacheMode: 'off' });
        if (cancelled || scopeRef.current !== scope) throw new Error('Authenticated identity changed');
        setApiToken(token);
        return token;
      } catch (refreshError) {
        if (!cancelled && scopeRef.current === scope && isSessionError(refreshError)) void startCleanLogin();
        throw refreshError;
      }
    };
    setTokenRefreshCallback(refreshToken);
    if (!ready) return () => { cancelled = true; setTokenRefreshCallback(null); };
    const refreshInterval = setInterval(() => {
      void refreshToken().catch((refreshError) => console.warn('Periodic token refresh failed:', refreshError));
    }, 45 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
      setTokenRefreshCallback(null);
    };
  }, [generation, getAccessTokenSilently, ready, startCleanLogin, subject]);

  if (fatalError?.subject === subject && fatalError?.generation === generation) {
    return (
      <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">{fatalError.message}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Reload dashboard</Button>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Signing you in…</Typography>
      </Box>
    );
  }

  return <TokenContext.Provider value={true}>{children}</TokenContext.Provider>;
};
